import { useCallback, useEffect, useRef, useState } from "react";

import { parseDetail } from "../utils/errors";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import ChecklistIcon from "@mui/icons-material/Checklist";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import KeyIcon from "@mui/icons-material/Key";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { useTenant } from "../hooks/useTenant";
import useRequests from "../hooks/http";

interface EditUserForm {
  role: string;
  temp_password: string;
}

interface AddUserForm {
  email: string;
  first_name: string;
  last_name: string;
  username: string;
  role: string;
}

const emptyAddUserForm: AddUserForm = {
  email: "",
  first_name: "",
  last_name: "",
  username: "",
  role: "",
};

function ApplicationAccessDrawer({
  open,
  user,
  tenantId,
  applications,
  onClose,
}: {
  open: boolean;
  user: TenantMember | null;
  tenantId: number;
  applications: Application[];
  onClose: () => void;
}) {
  const { get, post, del } = useRequests();
  const [accessIds, setAccessIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedAppId, setSelectedAppId] = useState<number | "">("");

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    const { data, status } = await get(
      `/admin/tenants/${tenantId}/users/${user.id}/access`,
    );
    if (status === 200 && Array.isArray(data)) setAccessIds(data);
    setLoading(false);
  }, [user, tenantId, get]);

  useEffect(() => {
    if (open && user) {
      load();
    } else {
      setAccessIds([]);
      setSelectedAppId("");
      setError("");
    }
  }, [open, user, load]);

  const handleGrant = async () => {
    if (!user || selectedAppId === "") return;
    setError("");
    const { status, data } = await post(
      `/admin/tenants/${tenantId}/users/${user.id}/access`,
      null,
      {
        application_id: selectedAppId,
      },
    );
    if (status === 204 || status === 200 || status === 201) {
      setAccessIds((prev) => [...prev, selectedAppId as number]);
      setSelectedAppId("");
    } else {
      setError(parseDetail(data?.detail, "Failed to grant access"));
    }
  };

  const handleRevoke = async (appId: number) => {
    if (!user) return;
    setError("");
    const { status, data } = await del(
      `/admin/tenants/${tenantId}/users/${user.id}/access/${appId}`,
    );
    if (status === 204 || status === 200) {
      setAccessIds((prev) => prev.filter((id) => id !== appId));
    } else {
      setError(parseDetail(data?.detail, "Failed to revoke access"));
    }
  };

  const grantedApps = applications.filter((a) => accessIds.includes(a.id));
  const ungrantedApps = applications.filter((a) => !accessIds.includes(a.id));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 460, p: 3 } }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">
          Application Access — {user?.first_name} {user?.last_name}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 3 }} />
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        <FormControl
          fullWidth
          size="small"
          disabled={ungrantedApps.length === 0}
        >
          <InputLabel>Grant access</InputLabel>
          <Select
            value={selectedAppId}
            label="Grant access"
            onChange={(e) => setSelectedAppId(e.target.value as number | "")}
          >
            {ungrantedApps.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleGrant}
          disabled={selectedAppId === "" || ungrantedApps.length === 0}
          sx={{ minWidth: 72 }}
        >
          Grant
        </Button>
      </Box>
      {ungrantedApps.length === 0 && grantedApps.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 2 }}
        >
          Access has been granted to all available applications.
        </Typography>
      )}
      {grantedApps.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No application access granted yet.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {grantedApps.map((app) => (
            <Box
              key={app.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 1,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box>
                <Typography variant="body2">{app.name}</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontFamily="monospace"
                >
                  {app.identifier}
                </Typography>
              </Box>
              <Button
                size="small"
                color="error"
                onClick={() => handleRevoke(app.id)}
              >
                Revoke
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Drawer>
  );
}

function AssignRolesDrawer({
  open,
  user,
  tenantId,
  roles,
  onClose,
}: {
  open: boolean;
  user: TenantMember | null;
  tenantId: number;
  roles: Role[];
  onClose: () => void;
}) {
  const { get, post, del } = useRequests();
  const [assignedRoleIds, setAssignedRoleIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState<number | "">("");

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    const { data, status } = await get(
      `/admin/tenants/${tenantId}/users/${user.id}/roles`,
    );
    if (status === 200 && Array.isArray(data)) setAssignedRoleIds(data);
    setLoading(false);
  }, [user, tenantId, get]);

  useEffect(() => {
    if (open && user) {
      load();
    } else {
      setAssignedRoleIds([]);
      setSelectedRoleId("");
      setError("");
    }
  }, [open, user, load]);

  const handleAssign = async () => {
    if (!user || selectedRoleId === "") return;
    setError("");
    const { status, data } = await post(
      `/admin/tenants/${tenantId}/users/${user.id}/roles`,
      null,
      {
        role_id: selectedRoleId,
      },
    );
    if (status === 204 || status === 200 || status === 201) {
      setAssignedRoleIds((prev) => [...prev, selectedRoleId as number]);
      setSelectedRoleId("");
    } else {
      setError(parseDetail(data?.detail, "Failed to assign role"));
    }
  };

  const handleRemove = async (roleId: number) => {
    if (!user) return;
    setError("");
    const { status, data } = await del(
      `/admin/tenants/${tenantId}/users/${user.id}/roles/${roleId}`,
    );
    if (status === 204 || status === 200) {
      setAssignedRoleIds((prev) => prev.filter((id) => id !== roleId));
    } else {
      setError(parseDetail(data?.detail, "Failed to remove role"));
    }
  };

  const assignedRoles = roles.filter((r) => assignedRoleIds.includes(r.id));
  const unassignedRoles = roles.filter(
    (r) => r.active && !assignedRoleIds.includes(r.id),
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 460, p: 3 } }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">
          Assign Roles — {user?.first_name} {user?.last_name}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 3 }} />
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Assign role</InputLabel>
          <Select
            value={selectedRoleId}
            label="Assign role"
            onChange={(e) => setSelectedRoleId(e.target.value as number | "")}
          >
            {unassignedRoles.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={selectedRoleId === ""}
          sx={{ minWidth: 80 }}
        >
          Assign
        </Button>
      </Box>
      {assignedRoles.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No roles assigned.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {assignedRoles.map((r) => (
            <Chip
              key={r.id}
              label={r.name}
              color="primary"
              variant="outlined"
              onDelete={() => handleRemove(r.id)}
            />
          ))}
        </Box>
      )}
    </Drawer>
  );
}

function AssignPermissionsDrawer({
  open,
  user,
  tenantId,
  permissions,
  applications,
  onClose,
}: {
  open: boolean;
  user: TenantMember | null;
  tenantId: number;
  permissions: Permission[];
  applications: Application[];
  onClose: () => void;
}) {
  const { get, post, del } = useRequests();
  const [assignedPermissionIds, setAssignedPermissionIds] = useState<number[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedAppId, setSelectedAppId] = useState<number | "">("");
  const [selectedPermissionId, setSelectedPermissionId] = useState<number | "">(
    "",
  );

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    const { data, status } = await get(
      `/admin/tenants/${tenantId}/users/${user.id}/permissions`,
    );
    if (status === 200 && Array.isArray(data)) setAssignedPermissionIds(data);
    setLoading(false);
  }, [user, tenantId, get]);

  useEffect(() => {
    if (open && user) {
      load();
    } else {
      setAssignedPermissionIds([]);
      setSelectedPermissionId("");
      setSelectedAppId("");
      setError("");
    }
  }, [open, user, load]);

  const handleAssign = async () => {
    if (!user || selectedPermissionId === "") return;
    setError("");
    const { status, data } = await post(
      `/admin/tenants/${tenantId}/users/${user.id}/permissions`,
      null,
      { permission_id: selectedPermissionId },
    );
    if (status === 204 || status === 200 || status === 201) {
      setAssignedPermissionIds((prev) => [
        ...prev,
        selectedPermissionId as number,
      ]);
      setSelectedPermissionId("");
    } else {
      setError(parseDetail(data?.detail, "Failed to assign permission"));
    }
  };

  const handleRemove = async (permissionId: number) => {
    if (!user) return;
    setError("");
    const { status, data } = await del(
      `/admin/tenants/${tenantId}/users/${user.id}/permissions/${permissionId}`,
    );
    if (status === 204 || status === 200) {
      setAssignedPermissionIds((prev) =>
        prev.filter((id) => id !== permissionId),
      );
    } else {
      setError(parseDetail(data?.detail, "Failed to remove permission"));
    }
  };

  const appPermissions =
    selectedAppId !== ""
      ? permissions.filter((p) => p.application_id === selectedAppId)
      : [];

  const assignedPermissions = permissions.filter((p) =>
    assignedPermissionIds.includes(p.id),
  );
  const unassignedInApp = appPermissions.filter(
    (p) => !assignedPermissionIds.includes(p.id),
  );

  const appForPermission = (p: Permission) =>
    applications.find((a) => a.id === p.application_id);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 460, p: 3 } }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">
          Assign Permissions — {user?.first_name} {user?.last_name}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 3 }} />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 2 }}
      >
        Direct permissions are assigned independently of roles and always appear
        in the JWT.
      </Typography>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Application</InputLabel>
        <Select
          value={selectedAppId}
          label="Application"
          onChange={(e) => {
            setSelectedAppId(e.target.value as number | "");
            setSelectedPermissionId("");
          }}
        >
          {applications.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        <FormControl fullWidth size="small" disabled={selectedAppId === ""}>
          <InputLabel>Permission</InputLabel>
          <Select
            value={selectedPermissionId}
            label="Permission"
            onChange={(e) =>
              setSelectedPermissionId(e.target.value as number | "")
            }
          >
            {unassignedInApp.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={selectedPermissionId === ""}
          sx={{ minWidth: 80 }}
        >
          Assign
        </Button>
      </Box>
      {assignedPermissions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No direct permissions assigned.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {assignedPermissions.map((p) => (
            <Box
              key={p.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box>
                <Typography variant="body2" fontFamily="monospace">
                  {p.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {appForPermission(p)?.name ?? "Unknown app"}
                </Typography>
              </Box>
              <Button
                size="small"
                color="error"
                onClick={() => handleRemove(p.id)}
              >
                Remove
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Drawer>
  );
}

function UserActionMenu({
  user,
  onEdit,
  onAssignRoles,
  onAssignPermissions,
  onApplicationAccess,
  onDeactivate,
  onRestore,
  onDelete,
}: {
  user: TenantMember;
  onEdit: () => void;
  onAssignRoles: () => void;
  onAssignPermissions: () => void;
  onApplicationAccess: () => void;
  onDeactivate: () => void;
  onRestore: () => void;
  onDelete: () => void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const handle = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <>
      <IconButton size="small" ref={anchorRef} onClick={() => setOpen(true)}>
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
      >
        {user.active && (
          <MenuItem onClick={() => handle(onEdit)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {user.active && <Divider />}
        {user.active && (
          <MenuItem onClick={() => handle(onAssignRoles)}>
            <ListItemIcon>
              <AssignmentIndIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Assign Roles</ListItemText>
          </MenuItem>
        )}
        {user.active && (
          <MenuItem onClick={() => handle(onAssignPermissions)}>
            <ListItemIcon>
              <ChecklistIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Assign Permissions</ListItemText>
          </MenuItem>
        )}
        {user.active && (
          <MenuItem onClick={() => handle(onApplicationAccess)}>
            <ListItemIcon>
              <KeyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Application Access</ListItemText>
          </MenuItem>
        )}
        {user.active && <Divider />}
        {user.active && (
          <MenuItem
            onClick={() => handle(onDeactivate)}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <PersonOffIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Deactivate</ListItemText>
          </MenuItem>
        )}
        {!user.active && (
          <MenuItem onClick={() => handle(onRestore)}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Restore</ListItemText>
          </MenuItem>
        )}
        {!user.active && <Divider />}
        {!user.active && (
          <MenuItem
            onClick={() => handle(onDelete)}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

export default function Users() {
  const { t } = useTranslation();
  const { get, post, patch, del } = useRequests();
  const { activeTenant, loading: tenantLoading } = useTenant();
  const [users, setUsers] = useState<TenantMember[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [userRoleCounts, setUserRoleCounts] = useState<Record<number, number>>(
    {},
  );
  const [loading, setLoading] = useState(true);

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserForm, setAddUserForm] = useState<AddUserForm>(emptyAddUserForm);
  const [addUserError, setAddUserError] = useState("");

  const [deleteError, setDeleteError] = useState("");

  const [editUser, setEditUser] = useState<TenantMember | null>(null);
  const [editForm, setEditForm] = useState<EditUserForm>({
    role: "",
    temp_password: "",
  });
  const [editError, setEditError] = useState("");

  const [rolesDrawerUser, setRolesDrawerUser] = useState<TenantMember | null>(
    null,
  );
  const [permissionsDrawerUser, setPermissionsDrawerUser] =
    useState<TenantMember | null>(null);
  const [accessDrawerUser, setAccessDrawerUser] = useState<TenantMember | null>(
    null,
  );

  const refreshRoleCount = useCallback(
    async (userId: number) => {
      if (!activeTenant) return;
      const { data, status } = await get(
        `/admin/tenants/${activeTenant.id}/users/${userId}/roles`,
      );
      if (status === 200 && Array.isArray(data)) {
        setUserRoleCounts((prev) => ({ ...prev, [userId]: data.length }));
      }
    },
    [activeTenant, get],
  );

  useEffect(() => {
    if (!activeTenant) {
      setUsers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setDeleteError("");
    Promise.all([
      get(`/admin/tenants/${activeTenant.id}/users`),
      get(`/admin/tenants/${activeTenant.id}/roles`),
      get(`/admin/tenants/${activeTenant.id}/applications`),
      get(`/admin/tenants/${activeTenant.id}/permissions`),
    ]).then(async ([usersRes, rolesRes, appsRes, permsRes]) => {
      if (usersRes.status === 200 && Array.isArray(usersRes.data)) {
        setUsers(usersRes.data);
        const counts = await Promise.all(
          (usersRes.data as TenantMember[]).map(async (u) => {
            const { data, status } = await get(
              `/admin/tenants/${activeTenant.id}/users/${u.id}/roles`,
            );
            return [
              u.id,
              status === 200 && Array.isArray(data) ? data.length : 0,
            ] as const;
          }),
        );
        setUserRoleCounts(Object.fromEntries(counts));
      } else if (usersRes.status !== 200) {
        setDeleteError(
          parseDetail(usersRes.data?.detail, "Failed to load users"),
        );
      }
      if (rolesRes.status === 200 && Array.isArray(rolesRes.data))
        setRoles(rolesRes.data);
      if (appsRes.status === 200 && Array.isArray(appsRes.data))
        setApplications(appsRes.data);
      if (permsRes.status === 200 && Array.isArray(permsRes.data))
        setPermissions(permsRes.data);
      setLoading(false);
    });
  }, [activeTenant, get]);

  const handleEmailChange = (email: string) => {
    const localPart = email.split("@")[0];
    setAddUserForm((prev) => ({
      ...prev,
      email,
      username:
        prev.username === prev.email.split("@")[0] ? localPart : prev.username,
    }));
  };

  const handleAddUserOpen = () => {
    setAddUserForm(emptyAddUserForm);
    setAddUserError("");
    setAddUserOpen(true);
  };

  const handleAddUser = async () => {
    if (!activeTenant) return;
    setAddUserError("");

    const body: {
      email: string;
      username: string;
      first_name: string;
      last_name: string;
      role?: string;
    } = {
      email: addUserForm.email,
      username: addUserForm.username,
      first_name: addUserForm.first_name,
      last_name: addUserForm.last_name,
    };

    if (addUserForm.role.trim() !== "") {
      body.role = addUserForm.role.trim();
    }

    const { data, status } = await post(
      `/admin/tenants/${activeTenant.id}/users`,
      null,
      body,
    );
    if (status === 201) {
      setUsers((prev) => [...prev, data]);
      setAddUserOpen(false);
    } else {
      setAddUserError(parseDetail(data?.detail, "Failed to add user"));
    }
  };

  const handleEditOpen = (user: TenantMember) => {
    setEditUser(user);
    setEditForm({ role: user.role || "", temp_password: "" });
    setEditError("");
  };

  const handleEdit = async () => {
    if (!activeTenant || !editUser) return;
    setEditError("");

    const { data, status } = await patch(
      `/admin/tenants/${activeTenant.id}/users/${editUser.id}`,
      { role: editForm.role.trim() || null },
    );
    if (status !== 200) {
      setEditError(parseDetail(data?.detail, "Failed to update user"));
      return;
    }

    if (editForm.temp_password.trim()) {
      const pwRes = await post(
        `/admin/tenants/${activeTenant.id}/users/${editUser.id}/set-password`,
        null,
        { password: editForm.temp_password.trim() },
      );
      if (pwRes.status !== 200) {
        setEditError(
          parseDetail(
            pwRes.data?.detail,
            "Role updated but failed to set password",
          ),
        );
        return;
      }
    }

    setUsers((prev) =>
      prev.map((u) => (u.id === editUser.id ? { ...u, role: data.role } : u)),
    );
    setEditUser(null);
  };

  const handleDeactivate = async (userId: number) => {
    if (!activeTenant) return;
    setDeleteError("");
    const { data, status } = await del(
      `/admin/tenants/${activeTenant.id}/users/${userId}`,
    );
    if (status === 200) {
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, active: false } : u)),
      );
    } else {
      setDeleteError(parseDetail(data?.detail, "Failed to deactivate user"));
    }
  };

  const handleRestore = async (tenantMemberId: number) => {
    if (!activeTenant) return;
    setDeleteError("");
    const { data, status } = await post(
      `/admin/tenants/${activeTenant.id}/users/${tenantMemberId}/restore`,
      null,
      {},
    );
    if (status === 200) {
      setUsers((prev) =>
        prev.map((u) => (u.id === tenantMemberId ? { ...u, active: true } : u)),
      );
    } else {
      setDeleteError(parseDetail(data?.detail, "Failed to restore user"));
    }
  };

  const handleDelete = async (
    credentialUserId: number,
    tenantMemberId: number,
  ) => {
    if (!activeTenant) return;
    setDeleteError("");
    const { data, status } = await del(
      `/admin/tenants/${activeTenant.id}/credential-users/${credentialUserId}`,
    );
    if (status === 200) {
      setUsers((prev) => prev.filter((u) => u.id !== tenantMemberId));
    } else {
      setDeleteError(parseDetail(data?.detail, "Failed to delete user"));
    }
  };

  if (tenantLoading || loading) return <LinearProgress />;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h4">{t("users.title")}</Typography>
        {activeTenant && (
          <Button variant="contained" onClick={handleAddUserOpen}>
            {t("users.addButton")}
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("users.description")}
      </Typography>

      {deleteError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setDeleteError("")}
        >
          {deleteError}
        </Alert>
      )}

      {users.length === 0 ? (
        <Typography color="text.secondary">
          No members in this tenant yet.
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    {u.first_name} {u.last_name}
                  </TableCell>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{u.email || "-"}</TableCell>
                  <TableCell>
                    {(userRoleCounts[u.id] ?? 0) > 0 ? (
                      <Chip
                        size="small"
                        label={`${userRoleCounts[u.id]} ${userRoleCounts[u.id] === 1 ? "role" : "roles"}`}
                        variant="outlined"
                        color="primary"
                        onClick={() => setRolesDrawerUser(u)}
                        sx={{ cursor: "pointer" }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.disabled"
                        sx={{ cursor: "pointer" }}
                        onClick={() => setRolesDrawerUser(u)}
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={u.active ? "Active" : "Inactive"}
                      color={u.active ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(u.created).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <UserActionMenu
                      user={u}
                      onEdit={() => handleEditOpen(u)}
                      onAssignRoles={() => setRolesDrawerUser(u)}
                      onAssignPermissions={() => setPermissionsDrawerUser(u)}
                      onApplicationAccess={() => setAccessDrawerUser(u)}
                      onDeactivate={() => handleDeactivate(u.id)}
                      onRestore={() => handleRestore(u.id)}
                      onDelete={() => handleDelete(u.user_id, u.id)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("users.inviteHelp")}
          </Typography>
          {addUserError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addUserError}
            </Alert>
          )}
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            type="email"
            required
            value={addUserForm.email}
            onChange={(e) => handleEmailChange(e.target.value)}
          />
          <TextField
            label="First Name"
            fullWidth
            margin="normal"
            value={addUserForm.first_name}
            onChange={(e) =>
              setAddUserForm({ ...addUserForm, first_name: e.target.value })
            }
          />
          <TextField
            label="Last Name"
            fullWidth
            margin="normal"
            value={addUserForm.last_name}
            onChange={(e) =>
              setAddUserForm({ ...addUserForm, last_name: e.target.value })
            }
          />
          <TextField
            label="Username"
            fullWidth
            margin="normal"
            required
            value={addUserForm.username}
            onChange={(e) =>
              setAddUserForm({ ...addUserForm, username: e.target.value })
            }
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={addUserForm.role}
              label="Role"
              onChange={(e) =>
                setAddUserForm({ ...addUserForm, role: e.target.value })
              }
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="owner">Owner</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddUser}
            disabled={!addUserForm.email || !addUserForm.username}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editUser)}
        onClose={() => setEditUser(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={editForm.role}
              label="Role"
              onChange={(e) =>
                setEditForm({ ...editForm, role: e.target.value })
              }
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="owner">Owner</MenuItem>
            </Select>
          </FormControl>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            {t("users.roleHelp")}
          </Typography>
          <TextField
            label="Set Temporary Password"
            fullWidth
            margin="normal"
            type="password"
            placeholder="Leave blank to keep existing password"
            helperText={t("users.passwordHelp")}
            value={editForm.temp_password}
            onChange={(e) =>
              setEditForm({ ...editForm, temp_password: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {activeTenant && (
        <>
          <AssignRolesDrawer
            open={Boolean(rolesDrawerUser)}
            user={rolesDrawerUser}
            tenantId={activeTenant.id}
            roles={roles}
            onClose={() => {
              if (rolesDrawerUser) refreshRoleCount(rolesDrawerUser.id);
              setRolesDrawerUser(null);
            }}
          />
          <AssignPermissionsDrawer
            open={Boolean(permissionsDrawerUser)}
            user={permissionsDrawerUser}
            tenantId={activeTenant.id}
            permissions={permissions}
            applications={applications}
            onClose={() => setPermissionsDrawerUser(null)}
          />
          <ApplicationAccessDrawer
            open={Boolean(accessDrawerUser)}
            user={accessDrawerUser}
            tenantId={activeTenant.id}
            applications={applications}
            onClose={() => setAccessDrawerUser(null)}
          />
        </>
      )}
    </Box>
  );
}
